/**
 * Version staleness monitor.
 *
 * Polls the LIVE frontend's `/version.json` (published by the deploy
 * workflow next to the bundle, never cached) every 2 minutes and compares
 * it with this bundle's compile-time build stamp. When a newer build is
 * live, this browser is running old JavaScript (typically a tab left open
 * across a deploy) and the user is prompted to hard-refresh.
 *
 * Frontend vs frontend on purpose: the backend deploys separately, so its
 * build legitimately differs after a backend-only release. (This used to
 * compare against the backend's /api/version, which read a file absent
 * from the backend image and answered "unknown" — the check never fired.)
 *
 * Consumers:
 *   - `<StaleVersionBanner>` at the top of every authed page
 *   - `<Sidebar>`'s Version pill (green when current, amber when stale)
 *
 * The hook is DOM-safe: it gracefully handles being called outside of
 * the provider (returns `isStale=false, live=null`) so we never crash
 * on the login screen or any other page that mounts before the
 * provider wraps the tree.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { APP_VERSION, APP_BUILD_SHA, APP_BUILD_TIME, IS_STAMPED_BUILD } from "@/version";

const POLL_INTERVAL_MS = 2 * 60 * 1000;       // every 2 minutes
const INITIAL_DELAY_MS = 15 * 1000;           // wait 15s after mount before first poll

const VersionContext = createContext({
  isStale: false,
  live: null,
  bundleVersion: APP_VERSION,
  dismissedForSession: false,
  dismiss: () => {},
  checkNow: async () => {},
});

// Is the live build (from /version.json) newer than this bundle?
// Exported for tests. A local/unstamped bundle never nags.
export function isNewerLiveBuild(live, bundleSha = APP_BUILD_SHA, bundleBuiltAt = APP_BUILD_TIME) {
  if (!live?.sha || !bundleSha) return false;
  if (live.sha === bundleSha) return false;
  const liveAt = Date.parse(live.built_at || "");
  const ourAt = Date.parse(bundleBuiltAt || "");
  // Only a NEWER build means "refresh": an older one is a deploy still
  // propagating (or a rollback) — no banner either way.
  if (Number.isNaN(liveAt) || Number.isNaN(ourAt)) return false;
  return liveAt > ourAt;
}

export function VersionProvider({ children }) {
  const [live, setLive] = useState(null);           // {version, sha, built_at}
  const [dismissed, setDismissed] = useState(false);

  const check = useCallback(async () => {
    if (!IS_STAMPED_BUILD) return;
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json();   // HTML (SPA fallback) throws → ignored
      if (data?.sha) setLive({ ...data, version: data.short || String(data.sha).slice(0, 7) });
    } catch {
      // Silent — the banner just stays hidden if the file is unreachable.
    }
  }, []);

  useEffect(() => {
    // Initial poll after a short delay so we don't race against the
    // user's first page load.
    const t0 = setTimeout(check, INITIAL_DELAY_MS);
    const t1 = setInterval(check, POLL_INTERVAL_MS);
    return () => { clearTimeout(t0); clearInterval(t1); };
  }, [check]);

  // If yet another build goes live after the user dismissed the banner,
  // clear the dismissal so they see the new prompt.
  useEffect(() => {
    if (live?.version && live.version !== APP_VERSION) {
      try {
        const dismissedFor = sessionStorage.getItem("eyvo_stale_dismissed_for");
        if (dismissedFor && dismissedFor !== live.version) {
          sessionStorage.removeItem("eyvo_stale_dismissed_for");
          setDismissed(false);
        } else if (dismissedFor === live.version) {
          setDismissed(true);
        }
      } catch { /* sessionStorage may be blocked */ }
    }
  }, [live?.version]);

  const isStale = isNewerLiveBuild(live);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      if (live?.version) {
        sessionStorage.setItem("eyvo_stale_dismissed_for", live.version);
      }
    } catch { /* ignore */ }
  }, [live?.version]);

  const value = {
    isStale,
    live,
    bundleVersion: APP_VERSION,
    dismissedForSession: dismissed,
    dismiss,
    checkNow: check,
  };
  return <VersionContext.Provider value={value}>{children}</VersionContext.Provider>;
}

export function useVersion() {
  return useContext(VersionContext);
}
